import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { extend, useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { DesignContext } from '../contexts/designContext.js';

extend({ OrbitControls });

export default forwardRef(({ viewMode = '3D', initialZoom = 3 }, forwardedRef) => {
  const { imageSizeScale = 1 } = useContext(DesignContext);
  const orbitRef = useRef();
  const { size, camera, gl, set } = useThree();

  const [isChanging, setIsChanging] = useState(false);

  useImperativeHandle(forwardedRef, () => ({
    setOrbitEnabled(isEnabled) {
      if (orbitRef.current.enabled !== isEnabled) {
        orbitRef.current.enabled = isEnabled;
        if (isEnabled) orbitRef.current.reset();
        else orbitRef.current.saveState();
      }
    },
    isChanging() {
      return isChanging;
    },
    getCamera() {
      return camera;
    },
    getAzimuthalAngle() {
      return orbitRef.current?.getAzimuthalAngle?.() ?? 0;
    }
  }));

  useFrame((state) => orbitRef.current.update());

  const { width, height } = size;

  const camera2D = useMemo(() => {
    let verticalOffset = 0;
    if (imageSizeScale > 1) {
      const bounds = gl.domElement.getBoundingClientRect();
      const browserCenter = window.innerHeight / 2 - bounds.top;
      const housePosition = Math.min(height / 2, Math.max(height * 0.15, browserCenter));
      verticalOffset = (housePosition - height / 2) / initialZoom;
    }
    const newCamera = new THREE.OrthographicCamera(
      width / -2,
      width / 2,
      height / 2 + verticalOffset,
      height / -2 + verticalOffset,
      -200,
      1024 * imageSizeScale
    );
    newCamera.position.set(0, 100, 0);
    newCamera.up.set(0, 1, 0);
    newCamera.lookAt(0, 0, 0);
    newCamera.zoom = initialZoom;
    newCamera.userData.baseZoom = initialZoom;
    newCamera.updateProjectionMatrix();
    return newCamera;
  }, [width, height, initialZoom, imageSizeScale, gl.domElement]);

  const camera3D = useMemo(() => {
    const newCamera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1024 * imageSizeScale);
    newCamera.position.set(0, 100, 200);
    newCamera.up.set(0, 1, 0);
    newCamera.lookAt(0, 0, 0);
    newCamera.fov = 60;
    newCamera.far = 1024 * imageSizeScale;
    newCamera.updateProjectionMatrix();
    return newCamera;
  }, [width, height, imageSizeScale]);

  useEffect(() => {
    if (!orbitRef.current) return;
    const v2D = viewMode !== '3D';
    if (v2D) {
      camera2D.position.set(0, 100, 0);
      camera2D.up.set(0, 1, 0);
      camera2D.lookAt(0, 0, 0);
      camera2D.zoom = initialZoom;
      camera2D.userData.baseZoom = initialZoom;
      camera2D.updateProjectionMatrix();
      set({ camera: camera2D });
    } else {
      set({ camera: camera3D });
    }
  }, [viewMode, initialZoom, camera2D, camera3D, set]);

  return (
    <orbitControls
      ref={orbitRef}
      args={[camera, gl.domElement]}
      autoRotate={false}
      enableDamping
      dampingFactor={0.05}
      screenSpacePanning
      maxZoom={40}
      minZoom={2 / imageSizeScale}
      maxPolarAngle={Math.PI / 2 - 0.05}
      maxDistance={500 * imageSizeScale}
      enableRotate={viewMode === '3D'}
    />
  );
});
